import React, { useState, useEffect } from "react";
import { Collapse, Card, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import ResultDisplay from "components/FlipPanel/components/ResultDisplay";
import AiOverviewTable from "components/MarkdownRender/tables/AiOverviewTable";

const CustomisedContentDraftSection = ({ 
  isOpen, 
  onToggle, 
  initialKeyword = "",
  initialFormData = null,
  onSubmit,
  onAiOverviewSubmit,
  isLoading = false,
  isAiOverviewLoading = false,
  isAnyLoading = false,
  draftInputs = null,
  draftResult = null,
  aiOverviewResult = null,
  onCopy = null
}) => {
  const [seoOutline, setSeoOutline] = useState(initialFormData?.seoOutline || "");
  const [keyword, setKeyword] = useState(initialFormData?.keyword || initialKeyword);
  const [noQueries, setNoQueries] = useState(5);


  useEffect(() => {
    setKeyword(initialKeyword);
  }, [initialKeyword]);

  useEffect(() => {
    if (initialFormData) {
      setSeoOutline(initialFormData.seoOutline || "");
      setKeyword(initialFormData.keyword || initialKeyword);
    }
  }, [initialFormData, initialKeyword]);

  const handleSubmit = () => {
    if (!seoOutline.trim() || !keyword.trim()) return;
    
    onSubmit({
      seo_outline: seoOutline.trim(),
      primary_keyword: keyword.trim()
    });
  };

  const handleGenerateAiOverview = () => {
    if (!draftResult || isAnyLoading) return;
    
    // Call the parent's onAiOverviewSubmit to handle the API call and state management
    if (onAiOverviewSubmit) {
      onAiOverviewSubmit({
        text: draftResult,
        no_queries: noQueries
      });
    }
  };

  const handleReset = () => {
    setSeoOutline("");
    setNoQueries(5);
  };


  return (
    <div className="mb-3">
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-0 p-0">
          <Button
            className="w-100 text-start border-0 bg-transparent text-dark d-flex align-items-center justify-content-between p-3"
            onClick={onToggle}
            aria-controls="collapse-draft"
            aria-expanded={isOpen}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={isOpen ? faChevronDown : faChevronRight}
                className="me-3 text-primary"
              />
              <div>
                <div className="fw-bold">Customised Content Draft</div>
                <small className="text-muted">Generate full customised content draft</small>
              </div>
            </div>
          </Button>
        </Card.Header>
        <Collapse in={isOpen}>
          <div id="collapse-draft">
            <Card.Body className="pt-0">
              <p className="text-muted small mb-3">
                Generate a complete customised content draft from your content outline and keyword.
              </p>
              
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Primary Keyword</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter primary keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    size="sm"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold">Content Outline</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Paste your content outline here..."
                    value={seoOutline}
                    onChange={(e) => setSeoOutline(e.target.value)}
                    size="sm"
                  />
                  <Form.Text className="text-muted">
                    Paste the content outline generated from the Content Outline section above.
                  </Form.Text>
                </Form.Group>
                
                <div className="row g-2">
                  <div className="col-8">
                    <div className="d-grid">
                      <Button 
                        variant="info" 
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!seoOutline.trim() || !keyword.trim() || isAnyLoading}
                        style={{
                          cursor: (!seoOutline.trim() || !keyword.trim() || isAnyLoading) ? 'not-allowed' : 'pointer',
                          opacity: (!seoOutline.trim() || !keyword.trim() || isAnyLoading) ? 0.7 : 1,
                          backgroundColor: (!seoOutline.trim() || !keyword.trim() || isAnyLoading) ? '#6c757d' : '',
                          borderColor: (!seoOutline.trim() || !keyword.trim() || isAnyLoading) ? '#6c757d' : '',
                          color: (!seoOutline.trim() || !keyword.trim() || isAnyLoading) ? '#ffffff' : ''
                        }}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Generating...
                          </>
                        ) : (
                          "Generate Draft"
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="d-grid">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleReset}
                        disabled={isAnyLoading || !seoOutline.trim()}
                        style={{
                          cursor: (isAnyLoading || !seoOutline.trim()) ? 'not-allowed' : 'pointer',
                          opacity: (isAnyLoading || !seoOutline.trim()) ? 0.7 : 1,
                          backgroundColor: (isAnyLoading || !seoOutline.trim()) ? '#f8f9fa' : '',
                          borderColor: (isAnyLoading || !seoOutline.trim()) ? '#6c757d' : '',
                          color: (isAnyLoading || !seoOutline.trim()) ? '#6c757d' : ''
                        }}
                      >
                        Reset
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              {/* Generated Draft Display */}
              <ResultDisplay
                result={draftResult}
                title="Generated Custom Draft"
                type="markdown"
                inputParameters={draftInputs}
                inputParametersLabel="Draft was generated with"
                className="mt-4"
                onCopy={onCopy}
              />

              {/* AI Overview Generation Section */}
              {draftResult && (
                <div className="mt-3">
                  <div className="row mb-3">
                    <div className="col-6">
                      <Form.Label className="small fw-bold">Number of Queries</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="5"
                        value={noQueries}
                        onChange={(e) => setNoQueries(parseInt(e.target.value) || 5)}
                        size="sm"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                  
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleGenerateAiOverview}
                    disabled={!draftResult || isAnyLoading}
                    className="me-2"
                    style={{
                      cursor: (!draftResult || isAnyLoading) ? 'not-allowed' : 'pointer',
                      opacity: (!draftResult || isAnyLoading) ? 0.7 : 1
                    }}
                  >
                    {isAiOverviewLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating AI Overview...
                      </>
                    ) : aiOverviewResult ? (
                      "AI Overview Generated"
                    ) : (
                      "Generate AI Overview"
                    )}
                  </Button>
                  <small className="text-muted">
                    Generate an AI overview from the content draft
                  </small>
                </div>
              )}

              {/* Generated AI Overview Display */}
              <AiOverviewTable
                result={aiOverviewResult}
                title="Generated AI Overview"
                onCopy={onCopy}
              />

            </Card.Body>
          </div>
        </Collapse>
      </Card>
    </div>
  );
};

export default CustomisedContentDraftSection;
